import type { Client, RequestCorrelationContext } from "@octopusdeploy/octopus-server-client";
import { Repository } from "@octopusdeploy/octopus-server-client";
import { semanticConventions, tracers } from "@octopusdeploy/telemetry";
import type { Context } from "@opentelemetry/api";
import { SpanStatusCode } from "@opentelemetry/api";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 } from "uuid";
import { useCorrelationContext } from "../CorrelationContext";
import { useOctopusClient } from "../OctopusClientContext";
import { getSpanContext } from "../getSpanContext";

interface Stream<Input extends unknown[]> {
    /**
     * Executes a streaming query. Responses are streamed back as events, handled in the onEventReceived parameter passed to the useStreamingQuery hook.
     * N.B. This will fail catastrophically if you try to execute the query multiple times concurrently. Avoid this by disabling user interaction when a
     * query is already running (e.g., disable a button when isLoading is true).
     * @param {unknown} input Input parameters that are passed from the location where the query is invoked (e.g., from a button click or other user input)
     * @returns {Promise<void>} A promise that completes when the streaming request has completed. Allows components like buttons to subscribe to the state of the request.
     */
    executeQuery: (...input: Input) => Promise<void>;
    /**
     * Returns true when the query is currently executing and streaming back events.
     */
    isLoading: boolean;
    /**
     * Returns an error if the query failed at any point. Gets cleared if the query is run again.
     */
    error: Error | null;
}

/**
 * @template StreamEvent The event generated in response to the query
 * @template Input Any input values used to generate the query
 */
interface StreamConfiguration<Input extends unknown[], StreamEvent> {
    /**
     * Each streamingQuery needs a name to describe what it is doing.
     * This is used as part of the correlation context and is available as part of traces and logs
     * Examples: "Get agent responses", "Get deployment progress", "Get task progress"
     */
    name: string;
    /**
     * A function that generates a query and provides access to a streamed response.
     * State related to received events should be managed by consumers and will be provided through `onEventReceived`.
     * @param {Repository} repository Any requests from this repository will be cancelled if the component unmounts while onEventReceived is executing
     * @param {Input} any Input parameters that are passed from the location where the query is invoked (e.g., from a button click or other user input)
     * @returns {AsyncIterable<StreamEvent>} An `AsyncIterable` representing the events to be received from the server.
     */
    executeQuery: (repository: Repository, ...input: Input) => AsyncIterable<StreamEvent>;
    /**
     * A function to execute when an event is received from the stream. Commonly used to assign the events to local component state.
     * @param {StreamEvent} event An event received from the stream (if any)
     * @returns {void}
     */
    onEventReceived: (event: StreamEvent) => void;
}

export function useStreamingQuery<Input extends unknown[], StreamEvent>(config: StreamConfiguration<Input, StreamEvent>): Stream<Input> {
    const streamHookInstanceId = useStreamHookInstanceId();
    const [streamState, setStreamState] = useStreamState();
    const client = useOctopusClient();

    // Commonly, people will supply config values in a way that recreates the config object every render
    // We don't want this to trigger a new instance of the executeQuery callback, because that should remain stable
    // But we do want the executeQuery callback to reflect the latest values, in case the provided config values happen to close over important state
    const configRef = useRef(config);
    // Setting the current value of the ref is a little bit naughty according to the react docs, which state
    // "Do not write or read ref.current during rendering."
    // But I think it is appropriate in this case.
    configRef.current = config;

    const correlationContextFromProvider = useCorrelationContext();
    const correlationContext = useMemo(
        () =>
            ({
                ...correlationContextFromProvider,
                ["frontend.streaming_query.instance.id"]: streamHookInstanceId,
                ["frontend.streaming_query.name"]: configRef.current.name,
            }) satisfies RequestCorrelationContext,
        [correlationContextFromProvider, streamHookInstanceId]
    );

    const abortSignal = useAbortSignal();

    const executeQueryCallback = useCallback(
        async (...input: Input) => {
            // We obtain the current value of the config ref here, rather than invoke the properties off the ref inside executeStream
            // In practice, this means that onEventReceived will be invoked as it existed when the stream _started_.
            return await executeQuery(setStreamState, configRef.current, client, correlationContext, abortSignal, ...input);
        },
        [abortSignal, client, correlationContext, setStreamState] // These should all be stable dependencies across the lifetime of the hook
    );

    return {
        executeQuery: executeQueryCallback,
        isLoading: streamState.isStreaming,
        error: streamState.error,
    };
}

async function executeQuery<Input extends unknown[], Output>(streamActions: StreamActions, config: StreamConfiguration<Input, Output>, client: Client, streamCorrelationContext: RequestCorrelationContext, abortSignal: AbortSignal, ...input: Input) {
    const streamInvocationId = v4();
    const streamInvocationCorrelationContext: RequestCorrelationContext = { ["frontend.streaming_query.invocation.id"]: streamInvocationId, ...streamCorrelationContext };
    const streamSpan = tracers.reactClient.startSpan(`Streaming query: ${config.name}`);
    streamSpan.setAttributes(streamInvocationCorrelationContext);
    streamSpan.setAttribute(semanticConventions.operationType, "Streaming query");

    const abortStreamSpan = () => streamSpan.addEvent("aborted");
    abortSignal.addEventListener("abort", abortStreamSpan);

    try {
        streamActions.start();
        const events = config.executeQuery(createRepository(client, streamCorrelationContext, getSpanContext(streamSpan), abortSignal), ...input);

        for await (const event of events) {
            if (abortSignal.aborted) {
                break;
            }
            config.onEventReceived(event);
        }
    } catch (error: unknown) {
        if (abortSignal.aborted) {
            throw error;
        }
        const errorObject: Error = error instanceof Error ? error : new Error(`Unknown error: ${JSON.stringify(error)}`);
        streamActions.error(errorObject);
        streamSpan.recordException(errorObject);
        streamSpan.setStatus({
            code: SpanStatusCode.ERROR,
            message: errorObject.message,
        });
        throw error;
    } finally {
        streamSpan.end();
        abortSignal.removeEventListener("abort", abortStreamSpan);
    }
    if (abortSignal.aborted) {
        return;
    }
    streamActions.finish();
}

function createRepository(client: Client, correlationContext: RequestCorrelationContext, context: Context, abortSignal: AbortSignal) {
    return new Repository(client, {
        correlationContext,
        abortSignal,
        openTelemetryContext: context,
    });
}

function useAbortSignal() {
    const abortController = useMemo(() => new AbortController(), []);
    useEffect(
        () => () => {
            if (!abortController.signal.aborted) {
                abortController.abort();
            }
        },
        [abortController]
    );
    return abortController.signal;
}

function useStreamState(): [StreamState, StreamActions] {
    const [state, setState] = useState<StreamState>(initialStreamState);

    const actions: StreamActions = useMemo(() => {
        return {
            start() {
                setState((prev) => {
                    if (prev.isStreaming) {
                        // This is a strict way of preventing race conditions.
                        // We might want to change this in future to a different, less catastrophic option (e.g., obsolete requests get cancelled, either automatically or manually).
                        // In practice, we should be avoiding these scenarios by disabling buttons etc.
                        throw new Error("Can't start a new streaming query when an existing one is still executing.");
                    }
                    return { isStreaming: true, error: null };
                });
            },
            error: (error: Error) => {
                setState({ error, isStreaming: false });
            },
            finish: () => setState((prev) => initialStreamState),
        };
    }, []);

    return [state, actions];
}

type StreamState = IsStreamingState | HasErrorsStreamState | IsNotStreamingState;
type IsStreamingState = { isStreaming: true; error: null };
type HasErrorsStreamState = { isStreaming: false; error: Error };
type IsNotStreamingState = { isStreaming: false; error: null };

const initialStreamState: IsNotStreamingState = { isStreaming: false, error: null };

interface StreamActions {
    start(): void;
    finish(): void;
    error(error: Error): void;
}

function useStreamHookInstanceId(): string {
    return useMemo(() => v4(), []);
}
